import { useState, useEffect, useRef } from 'react';
import { Button } from '../common/Button';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';

const FIB_DELAYS = [60, 120, 180, 300, 480, 780, 1260]; // in seconds: 1,2,3,5,8,13,21 min

interface ConfirmEmailProps {
  email: string;
  onResend: () => Promise<void>;
  descriptionKey?: string;
  backToPath?: string;
  backToTextKey?: string;
  resendTextKey?: string;
  translationNamespace?: string;
}

export default function ConfirmEmail({
  email,
  onResend,
  descriptionKey = 'confirmEmail.description',
  backToPath = '/',
  backToTextKey = 'confirmEmail.back',
  resendTextKey = 'confirmEmail.resend',
  translationNamespace = 'auth',
}: ConfirmEmailProps) {
  const [resendCount, setResendCount] = useState(0);
  const [remaining, setRemaining] = useState(60);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation(translationNamespace);

  useEffect(() => {
    if (remaining > 0) {
      timerRef.current = setTimeout(() => setRemaining(remaining - 1), 1000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [remaining]);

  const handleResend = async () => {
    await onResend();
    const nextDelay = FIB_DELAYS[Math.min(resendCount + 1, FIB_DELAYS.length - 1)];
    setResendCount(resendCount + 1);
    setRemaining(nextDelay);
  };

  return (
    <div className="flex flex-col py-14 md:min-h-[60vh] md:justify-center md:items-center">
      <div>
        <h2 className="text-4xl font-bold text-secondary mb-4">{t('confirmEmail.title')}</h2>
        <p className="text-secondary font-ambit text-[1.0625rem] font-normal leading-[1.5625rem] max-w-md mb-8">
          <Trans
            i18nKey={descriptionKey}
            ns={translationNamespace}
            values={{ email }}
            components={{ bold: <span className="font-bold" /> }}
          />
        </p>
        <Button
          onClick={handleResend}
          disabled={remaining > 0}
          className="w-full md:w-auto md:px-8 md:py-2"
        >
          {remaining > 0
            ? t('confirmEmail.resendIn', { minutes: Math.ceil(remaining / 60) })
            : t(resendTextKey)}
        </Button>
        <Link
          to={backToPath}
          className="block mx-auto text-secondary font-bold underline mt-8 text-center md:text-left"
        >
          {t(backToTextKey)}
        </Link>
      </div>
    </div>
  );
}
